import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

function ProntuarioDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    
    const [prontuario, setProntuario] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    const [isEditing, setIsEditing] = useState(false);

    const [userRole, setUserRole] = useState(null);
    
    const [editedTexts, setEditedTexts] = useState({
        texto_livre: '',
        texto_psicologo: '',
        texto_psiquiatra: ''
    });

    const [editedGeneral, setEditedGeneral] = useState({
        nome: '',
        data_nascimento: '',
        idade: '',
        sexo: '',
        cpf: '',
        telefone: '',
        telefone_emergencia: '',
        email: '',
        cidade_estado: '',
        estado_civil: '',
        profissao: '',
        empresa: ''
    });

    const fetchProntuario = () => {
        setIsLoading(true);
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/');
            return;
        }

        try {
            const decodedToken = jwtDecode(token);
            setUserRole(decodedToken.role);
        } catch (error) {
            navigate('/');
        }

        axios.get(`https://ipj-api.vercel.app/api/prontuarios/${id}`, {
            headers: { 'Authorization': token }
        })
        .then(response => {
            const data = response.data;
            setProntuario(data);
            setEditedTexts({
                texto_livre: data.texto_livre || '',
                texto_psicologo: data.texto_psicologo || '',
                texto_psiquiatra: data.texto_psiquiatra || ''
            });
            setEditedGeneral({
                nome: data.nome || '',
                data_nascimento: data.data_nascimento ? data.data_nascimento.split('T')[0] : '', // Formata YYYY-MM-DD para o input type="date"
                idade: data.idade || '',
                sexo: data.sexo || '',
                cpf: data.cpf || '',
                telefone: data.telefone || '',
                telefone_emergencia: data.telefone_emergencia || '',
                email: data.email || '',
                cidade_estado: data.cidade_estado || '',
                estado_civil: data.estado_civil || '',
                profissao: data.profissao || '',
                empresa: data.empresa || ''
            });
        })
        .catch(() => {
            navigate('/');
        })
        .finally(() => {
            setIsLoading(false);
        });
    };

    useEffect(() => {
        fetchProntuario();
    }, [id, navigate]);

    const handleEdit = () => {
        setIsEditing(true);
    };

    const handleCancel = () => {
        setIsEditing(false);
        setEditedTexts({
            texto_livre: prontuario.texto_livre || '',
            texto_psicologo: prontuario.texto_psicologo || '',
            texto_psiquiatra: prontuario.texto_psiquiatra || ''
        });
        setEditedGeneral({
            nome: prontuario.nome || '',
            data_nascimento: prontuario.data_nascimento ? prontuario.data_nascimento.split('T')[0] : '',
            idade: prontuario.idade || '',
            sexo: prontuario.sexo || '',
            cpf: prontuario.cpf || '',
            telefone: prontuario.telefone || '',
            telefone_emergencia: prontuario.telefone_emergencia || '',
            email: prontuario.email || '',
            cidade_estado: prontuario.cidade_estado || '',
            estado_civil: prontuario.estado_civil || '',
            profissao: prontuario.profissao || '',
            empresa: prontuario.empresa || ''
        });
    };

    const handleSave = () => {
        const token = localStorage.getItem('token');
        let requestBody = { ...editedGeneral };

        if (userRole === 'assistente') {
            requestBody.texto_livre = editedTexts.texto_livre;
        } else if (userRole === 'psicologo') {
            requestBody.texto_psicologo = editedTexts.texto_psicologo;
        } else if (userRole === 'psiquiatra') {
            requestBody.texto_psiquiatra = editedTexts.texto_psiquiatra;
        }
        
        axios.put(`https://ipj-api.vercel.app/api/prontuarios/${id}`, requestBody, {
            headers: { 'Authorization': token }
        })
        .then(() => {
            alert('Prontuário atualizado com sucesso!');
            setIsEditing(false);
            fetchProntuario();
        })
        .catch(() => {
            alert('Falha ao atualizar o prontuário. Tente novamente.');
        });
    };

    const handleTextChange = (field, value) => {
        setEditedTexts(prevState => ({
            ...prevState,
            [field]: value
        }));
    };

    const handleGeneralChange = (field, value) => {
        setEditedGeneral(prevState => ({
            ...prevState,
            [field]: value
        }));
    };

    if (isLoading) {
        return <div className="prontuario-detail-container">Carregando...</div>;
    }

    if (!prontuario) {
        return <div className="prontuario-detail-container">Prontuário não encontrado.</div>;
    }

    const buttonContainerStyle = { position: 'absolute', top: '80px', right: '20px' };
    const textareaStyle = { width: '100%', minHeight: '200px', marginTop: '5px', padding: '10px', boxSizing: 'border-box' };
    const inputStyle = { padding: '5px', margin: '5px 0', fontSize: '1em', width: '100%', boxSizing: 'border-box' };
    const fieldGroupStyle = { marginBottom: '10px' };
    const pStyle = { whiteSpace: 'pre-wrap', border: '1px solid #eee', padding: '10px', background: '#f9f9f9', minHeight: '50px', marginBottom: '20px' };
    
    const handleDelete = () => {
        const isConfirmed = window.confirm('Tem certeza que deseja deletar este prontuário? Esta ação não pode ser desfeita.');
        
        if (isConfirmed) {
            const token = localStorage.getItem('token');
            axios.delete(`https://ipj-api.vercel.app/api/prontuarios/${id}`, {
                headers: { 'Authorization': token }
            })
            .then(() => {
                alert('Prontuário deletado com sucesso!');
                navigate('/prontuarios'); 
            })
            .catch(() => {
                alert('Falha ao deletar o prontuário. Tente novamente.');
            });
        }
    };

    // Helper para exibir data bonitinha quando não estiver editando
    const displayDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('pt-BR', { timeZone: 'UTC' }); 
    };

    return (
        <div className="prontuario-detail-container" style={{ position: 'relative', padding: '20px' }}>
            <div style={buttonContainerStyle}>
                <Link to="/prontuarios" className="btn-back" style={{ display: `${isEditing ? 'none' : 'inline-block'}` }}>
                    &larr; Voltar para Lista
                </Link>
                {!isEditing ? (
                    <>
                    <button onClick={handleDelete} className="btn-cancel" style={{ marginRight: '10px' }}>Deletar</button>
                    <button onClick={handleEdit} className="btn-edit">Editar Prontuário</button>
                    </>
                ) : (
                    <>
                        <button onClick={handleSave} className="btn-save">Salvar</button>
                        <button onClick={handleCancel} className="btn-cancel">Cancelar</button>
                    </>
                )}
            </div>

            <h2 style={{ marginTop: '60px' }}>Detalhes do Prontuário</h2>

            <div style={{ marginBottom: '20px', maxWidth: '600px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div style={fieldGroupStyle}>
                    <strong>Nome: </strong>
                    {isEditing ? (
                        <input type="text" style={inputStyle} value={editedGeneral.nome} onChange={(e) => handleGeneralChange('nome', e.target.value)} />
                    ) : (
                        <span><br/>{prontuario.nome}</span>
                    )}
                </div>
                <div style={fieldGroupStyle}>
                    <strong>Data de Nascimento: </strong>
                    {isEditing ? (
                        <input type="date" style={inputStyle} value={editedGeneral.data_nascimento} onChange={(e) => handleGeneralChange('data_nascimento', e.target.value)} />
                    ) : (
                        <span><br/>{displayDate(prontuario.data_nascimento)}</span>
                    )}
                </div>
                <div style={fieldGroupStyle}>
                    <strong>Idade: </strong>
                    {isEditing ? (
                        <input type="number" style={inputStyle} value={editedGeneral.idade} onChange={(e) => handleGeneralChange('idade', e.target.value)} />
                    ) : (
                        <span><br/>{prontuario.idade || '-'}</span>
                    )}
                </div>
                <div style={fieldGroupStyle}>
                    <strong>Sexo: </strong>
                    {isEditing ? (
                        <input type="text" style={inputStyle} value={editedGeneral.sexo} onChange={(e) => handleGeneralChange('sexo', e.target.value)} />
                    ) : (
                        <span><br/>{prontuario.sexo || '-'}</span>
                    )}
                </div>
                <div style={fieldGroupStyle}>
                    <strong>CPF: </strong>
                    {isEditing ? (
                        <input type="text" style={inputStyle} value={editedGeneral.cpf} onChange={(e) => handleGeneralChange('cpf', e.target.value)} />
                    ) : (
                        <span><br/>{prontuario.cpf || '-'}</span>
                    )}
                </div>
                <div style={fieldGroupStyle}>
                    <strong>Telefone/WhatsApp: </strong>
                    {isEditing ? (
                        <input type="text" style={inputStyle} value={editedGeneral.telefone} onChange={(e) => handleGeneralChange('telefone', e.target.value)} />
                    ) : (
                        <span><br/>{prontuario.telefone || '-'}</span>
                    )}
                </div>
                <div style={fieldGroupStyle}>
                    <strong>Telefone de Emergência: </strong>
                    {isEditing ? (
                        <input type="text" style={inputStyle} value={editedGeneral.telefone_emergencia} onChange={(e) => handleGeneralChange('telefone_emergencia', e.target.value)} />
                    ) : (
                        <span><br/>{prontuario.telefone_emergencia || '-'}</span>
                    )}
                </div>
                <div style={fieldGroupStyle}>
                    <strong>E-mail: </strong>
                    {isEditing ? (
                        <input type="email" style={inputStyle} value={editedGeneral.email} onChange={(e) => handleGeneralChange('email', e.target.value)} />
                    ) : (
                        <span><br/>{prontuario.email || '-'}</span>
                    )}
                </div>
                <div style={fieldGroupStyle}>
                    <strong>Cidade/Estado: </strong>
                    {isEditing ? (
                        <input type="text" style={inputStyle} value={editedGeneral.cidade_estado} onChange={(e) => handleGeneralChange('cidade_estado', e.target.value)} />
                    ) : (
                        <span><br/>{prontuario.cidade_estado || '-'}</span>
                    )}
                </div>
                <div style={fieldGroupStyle}>
                    <strong>Estado Civil: </strong>
                    {isEditing ? (
                        <input type="text" style={inputStyle} value={editedGeneral.estado_civil} onChange={(e) => handleGeneralChange('estado_civil', e.target.value)} />
                    ) : (
                        <span><br/>{prontuario.estado_civil || '-'}</span>
                    )}
                </div>
                <div style={fieldGroupStyle}>
                    <strong>Profissão/Ocupação: </strong>
                    {isEditing ? (
                        <input type="text" style={inputStyle} value={editedGeneral.profissao} onChange={(e) => handleGeneralChange('profissao', e.target.value)} />
                    ) : (
                        <span><br/>{prontuario.profissao || '-'}</span>
                    )}
                </div>
                <div style={fieldGroupStyle}>
                    <strong>Empresa: </strong>
                    {isEditing ? (
                        <input type="text" style={inputStyle} value={editedGeneral.empresa} onChange={(e) => handleGeneralChange('empresa', e.target.value)} />
                    ) : (
                        <span><br/>{prontuario.empresa || '-'}</span>
                    )}
                </div>
            </div>
            
            <div>
                <p><strong>Anotações do Assistente:</strong></p>
                {(isEditing && userRole === 'assistente') ? (
                    <textarea style={textareaStyle} value={editedTexts.texto_livre} onChange={(e) => handleTextChange('texto_livre', e.target.value)} />
                ) : (
                    <p style={pStyle}>{prontuario.texto_livre || 'Nenhuma anotação.'}</p>
                )}

                <p><strong>Anotações do Psicólogo:</strong></p>
                {(isEditing && userRole === 'psicologo') ? (
                    <textarea style={textareaStyle} value={editedTexts.texto_psicologo} onChange={(e) => handleTextChange('texto_psicologo', e.target.value)} />
                ) : (
                    <p style={pStyle}>{prontuario.texto_psicologo || 'Nenhuma anotação.'}</p>
                )}

                <p><strong>Anotações do Psiquiatra:</strong></p>
                {(isEditing && userRole === 'psiquiatra') ? (
                    <textarea style={textareaStyle} value={editedTexts.texto_psiquiatra} onChange={(e) => handleTextChange('texto_psiquiatra', e.target.value)} />
                ) : (
                    <p style={pStyle}>{prontuario.texto_psiquiatra || 'Nenhuma anotação.'}</p>
                )}
            </div>

            <div style={{ marginTop: '20px', fontSize: '0.9em', color: '#555' }}>
                <p><strong>Data de Criação:</strong> {new Date(prontuario.data_criacao).toLocaleString()}</p>
                {prontuario.data_ultima_alteracao && (
                    <p>
                        <strong>Última Alteração:</strong> {new Date(prontuario.data_ultima_alteracao).toLocaleString()}
                        {prontuario.ultima_alteracao_por && <span> por <strong>{prontuario.ultima_alteracao_por.toUpperCase()}</strong></span>}
                    </p>
                )}
            </div>
        </div>
    );
}

export default ProntuarioDetail;